import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import moment from "moment";
import axios, { AxiosError, AxiosResponse } from "axios";
import { IEquipment } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";
import { IUser } from "../../User";
import { useAuth } from "../../../hooks/auth";
import filterOptions from "../../../utils/filterOptions";

const { Option } = Select;

export interface IEquipmentTypes {
  id: string;
  name: string;
}

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (equipment?: IEquipment[]) => void;
  equipment?: IEquipment;
}

interface IUserFormData {
  equipment_id: string;
  equipment_type: string;
  date_acquisition: string;
  status: "FREE" | "DEFECT" | "IN USE";
  description_defect: string | null;
  assignment: string | null;
  equipment_description: string;
  user_id: string | null;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const EquipmentModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  equipment,
}) => {
  const { t } = useTranslation(["consultants"]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [isSetManager, setIsSetManager] = useState("");
  const [userEquipment, setUserEquipment] = useState<IUser>();
  const [userId, setUserId] = useState<string | null>(null);
  const [equipmentType, setEquipmentType] = useState<IEquipmentTypes[]>([]);
  const { user: modificator } = useAuth();

  useEffect(() => setIsSetManager(equipment?.status as string), [equipment]);
  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!equipment, [equipment]);

  useEffect(() => {
    api
      .get("/equipments/types")
      .then(response => {
        setEquipmentType(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os tipos de equipamentos, tente novamente.",
        });
      });
  }, [addToast]);

  useEffect(() => {
    form.setFieldsValue({
      ...equipment,
      date_acquisition:
        equipment?.date_acquisition &&
        moment(equipment.date_acquisition.split("T")[0]),
    });
  }, [form, equipment]);

  const handleClose = useCallback(
    (usr?: IEquipment[]) => {
      form.resetFields();
      handleCloseModal(usr);
    },
    [form, handleCloseModal],
  );

  const formatDate = useCallback((date: any) => {
    if (!date) {
      return undefined;
    }
    return date.format("YYYY-MM-DD");
  }, []);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      api
        .get<IUser>(`/users/${userId}`)
        .then(response => {
          setUserEquipment(response.data);
        })
        .catch(() => {
          addToast({
            type: "error",
            title: "Erro na busca",
            description:
              "Occoreu um erro ao buscar os consultores, tente novamente.",
          });
        })
        .finally(() => setLoading(false));
    }
  }, [addToast, userId]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O equipamento não foi encontrado";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do equipamento, tente novamente"
            : "Ocorreu um erro ao criar o equipamento, tente novamente";
      }

      addToast({
        type: "error",
        title: t("requestEq.errorCreate"),
        description,
      });
    },
    [addToast, isEditing, t],
  );

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          order_name: "name",
          order_direction: "ASC",
        },
      })
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const equipmentTypesOptions = useMemo(() => {
    if (!equipmentType.length) {
      return undefined;
    }
    return equipmentType.map(eq => ({
      label: t(`equipments.${eq.name}`),
      value: eq.name,
    }));
  }, [equipmentType, t]);

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    return users.map(usr => ({
      label: usr.name,
      value: usr.id,
    }));
  }, [users]);

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      try {
        const schema = Yup.object().shape({
          equipment_id: Yup.string().required("ID do patrimonio obrigatório"),
          equipment_type: Yup.string().required(
            "Tipo de Equipamento obrigatório",
          ),
          date_acquisition: Yup.date().required(
            "Data de aquisição obrigatório",
          ),
          status: Yup.string().required("Data de aquisição obrigatório"),
          description_defect: Yup.string()
            .nullable()
            .when("status", {
              is: status => status === "DEFECT",
              then: Yup.string().required("descrição do defeito obrigatório"),
            }),
          user_id: Yup.string()
            .nullable()
            .when("status", {
              is: status => status === "IN USE",
              then: Yup.string().required("Usuário é obrigatório"),
            }),
          assignment: Yup.string().nullable(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const {
          equipment_id,
          equipment_type,
          date_acquisition,
          status,
          description_defect,
          equipment_description,
          user_id,
        } = data;

        const user = usersOptions?.filter(
          userSelect => userSelect.value === user_id,
        ) as { label: string; value: string }[];

        const payload = {
          equipment_id,
          equipment_type,
          status,
          description_defect,
          assignment: user[0]?.label || null,
          equipment_description,
          date_acquisition: formatDate(date_acquisition),
          user_id,
        };

        removeNullKeys(payload);

        let response: AxiosResponse;
        if (isEditing) {
          response = await api.patch(`/equipments/${equipment?.id}`, payload);

          setUserId(user_id);
          const amount = userEquipment?.equipament?.length
            ? userEquipment?.equipament?.length
            : 0;

          // eslint-disable-next-line no-nested-ternary
          const flag_equipment = amount > 1 ? 1 : user_id ? 1 : 0;

          const id = equipment?.user_id || user_id || null;

          if (id) {
            await api.patch(`/users/${id}/consultant`, {
              flag_equipment,
              modificatoryId: modificator.id,
            });
          }

          addToast({
            type: "success",
            title: t("requestEq.sucess"),
            description: t("requestEq.sucessDescription"),
          });
        } else {
          response = await api.post("/equipments", payload);
          const id = user_id;

          if (id) {
            await api.patch(`/users/${id}/consultant`, {
              flag_equipment: 1,
              modificatoryId: modificator.id,
            });
          }
          addToast({
            type: "success",
            title: t("requestEq.create"),
            description: t("requestEq.createDescription"),
          });
        }

        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      formatDate,
      generateErrorToast,
      handleClose,
      isEditing,
      equipment?.id,
      usersOptions,
      t,
    ],
  );

  return (
    <Modal
      title={isEditing ? t("modalEquipment.edit") : t("modalEquipment.create")}
      visible={visible}
      destroyOnClose
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="equipment_id"
          required
          label={t("equipments.equipment_id")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="equipment_type"
          required
          label={t("equipments.equipment_type")}
          errors={validationErrors}
        >
          <Select allowClear options={equipmentTypesOptions} />
        </FormItem>
        <FormItem
          name="equipment_description"
          required
          label={t("equipments.equipment_description")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        {/* {equipment?.assignment ? (
          <FormItem
            name="assignment"
            label={t("equipments.assignment")}
            errors={validationErrors}
          >
            <Input disabled />
          </FormItem>
        ) : (
          <></>
        )} */}
        {isSetManager === "DEFECT" ? (
          <FormItem
            name="description_defect"
            label={t("equipments.description_defect")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        ) : (
          <></>
        )}
        <FormItem
          name="status"
          required
          label={t("equipments.status")}
          errors={validationErrors}
        >
          <Select allowClear>
            <Option value="FREE">
              <p role="presentation" onClick={() => setIsSetManager("FREE")}>
                {t("equipments.FREE")}
              </p>
            </Option>
            <Option value="IN USE">
              <p role="presentation" onClick={() => setIsSetManager("IN USE")}>
                {t("equipments.IN_USE")}
              </p>
            </Option>
            <Option value="DEFECT">
              <p role="presentation" onClick={() => setIsSetManager("DEFECT")}>
                {t("equipments.DEFECT")}
              </p>
            </Option>
          </Select>
        </FormItem>
        <FormItem
          name="date_acquisition"
          label={t("equipments.equipment_acquisition")}
          errors={validationErrors}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </FormItem>
        <FormItem
          name="user_id"
          label={t("equipments.user_id")}
          errors={validationErrors}
          required={isSetManager === "IN USE"}
        >
          <Select
            allowClear
            showSearch
            filterOption={filterOptions}
            options={usersOptions}
            loading={loadingUsers}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default EquipmentModal;
